import { search } from "@ui/ComponentUtils/blueprintAPIs";
import commonConfig from "config/commonConfig";
import enums from "helpers/enums";
import { isEmpty } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import {
  getAllCategories,
  getClientProducts,
  getClientProductsPaging,
  getDivisionByClientId,
  getOrders,
  getOverview,
  getProductByCode,
  getSpecialProducts,
  getWishList,
  searchProducts,
  setPushNotifications,
} from "rest/rest";
import { useLocalstorageState } from "rooks";

import { getMainClient, reducerWithoutAction } from "utils/common/common.utils";
import { useMounted } from "utils/hooks/use-mounted.hook";
import localStorageUtils, {
  localStorageKeys,
} from "utils/local-utils/localstorage.utils";
import { useAppAuth } from "./auth-context";

const baseUrl = commonConfig.baseApiUrl;

const initialContext = {
  state: {},
  isLoading: false,
  errorMessage: "",
  handleStateChange: (newState) => {},
  fetchOverview: () => {},
  fetchDivisions: () => {},
  fetchClientProducts: () => {},
  fetchClientProductsPaging: (pageNo, perPageRecords) => {},
  fetchAllCategories: () => {},
  setSelectedProduct: (value) => {},
  setSelectedDivision: (value) => {},
  setSelectedCategory: (value) => {},
  addToCart: () => {},
  fetchProductByCode: () => {},
  fetchYourOrders: () => {},
  isOrderPlacing: false,
  isCartItemsAdded: false,
  setIsOrderPlacing: (isOrderd) => {},
  setIsCartItemsAdded: (isAdded) => {},
  searchCriteriaProducts: (
    searchText,
    pageNo,
    perPageRecords,
    category_id,
    sort
  ) => {},
  setSearchText: (searchText) => {},
  setIsWishListItemChanged: (wishlist) => {},
  isWishListItemChanged: false,
  fetchWishlist: () => {},
  setWishlistItems: (wishListItems) => {},
  wishListItems: {},
  clientDivisions: [],
  setClientDivisions: (clientDivisions) => {},
  fetchSpecialProducts: () => {},
  getAppPushNotifications: () => {},
  setIsWishListLoading: () => {},
  isWishListLoading: false,
  cartItemItems: [],
  setCartItemItems: (cartItemItems) => {},
  fetchYourOrdersById: (orderId) => {},
  setIsProductLoading: (isProductLoading) => {},
  isProductLoading: false,
  setIsSingleProductLoading: (isProductLoading) => {},
  isSingleProductLoading: false,
};

const APIContext = createContext(initialContext);

export const APIProvider = ({ children }) => {
  const [mainClient] = useState(getMainClient(commonConfig.clients));
  const isMounted = useMounted();
  const initialState = useMemo(
    () => ({
      overviews: {},
      divisions: [],
      clientProducts: [],
      selectedProduct: {},
      categories: [],
      selectedCategory: {},
      yourCartItems: {},
      yourOrders: [],
      orderItems: [],
      searchText: "",
      wishlists: {},
      specialProducts: [],
      notification: [],
      alerts: [],
    }),
    []
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isWishListLoading, setIsWishListLoading] = useState(false);
  const [isProductLoading, setIsProductLoading] = useState(false);
  const [isSingleProductLoading, setIsSingleProductLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [state, dispatch] = useReducer(reducerWithoutAction, initialState);
  const [isCartItemsAdded, setIsCartItemsAdded] = useState(false);
  const [wishListItems, setWishlistItems] = useLocalstorageState(
    localStorageKeys.wishListItems,
    {}
  );
  const [cartItemItems, setCartItemItems] = useLocalstorageState(
    localStorageKeys.cartItems,
    {}
  );
  const [clientDivisions, setClientDivisions] = useLocalstorageState(
    localStorageKeys.divisions,
    {}
  );

  const [isWishListItemChanged, setIsWishListItemChanged] =
    useLocalstorageState(localStorageKeys.isWishlistAdded, false);

  const [isOrderPlacing, setIsOrderPlacing] = useState(false);
  //   const [searchText, setSearchText] = useState("");

  const handleStateChange = useCallback((newState) => {
    dispatch(newState);
  }, []);

  const setSearchText = useCallback((searchText) => {
    handleStateChange({
      searchText: searchText,
    });
  }, []);

  const { isAuthenticated } = useAppAuth();
  const fetchOverview = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getOverview();
      if (isMounted.current) {
        handleStateChange({
          overviews: response,
        });
        localStorageUtils.set(localStorageKeys);
      }
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchDivisions = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getDivisionByClientId();

      const alreadyDivisionExists = localStorageUtils?.get(
        localStorageKeys.division
      );
      if (isEmpty(alreadyDivisionExists)) {
        setSelectedDivision(response[0]);
      }
      if (isMounted.current) {
        handleStateChange({
          divisions: response,
        });

        setClientDivisions(response || {});
      }

      return response;
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, [isMounted]);

  const fetchAllCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getAllCategories();
      if (isMounted.current) {
        handleStateChange({
          categories: response,
        });
      }
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchClientProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getClientProducts();

      if (isMounted.current) {
        handleStateChange({
          clientProducts: response,
        });
      }
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchClientProductsPaging = useCallback(
    async (pageNo, perPageRecords, category_id, sort) => {
      try {
        setIsProductLoading(true);
        const response = await getClientProductsPaging(
          pageNo,
          perPageRecords,
          category_id,
          sort
        );
        if (isMounted.current) {
          handleStateChange({
            clientProducts: response,
          });
        }
      } catch (err) {
        setErrorMessage(err);
      } finally {
        setIsProductLoading(false);
      }
    },
    []
  );

  const fetchProductByCode = useCallback(async (code) => {
    try {
      setIsSingleProductLoading(true);
      const response = await getProductByCode(code);

      if (isMounted.current) {
        handleStateChange({
          selectedProduct: response,
        });
      }
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setIsSingleProductLoading(false);
    }
  }, []);

  const fetchYourOrders = useCallback(async (sortMethod) => {
    handleStateChange({
      yourOrders: [],
    });
    const userId = localStorageUtils.get(localStorageKeys.userDetails)?._id;
    try {
      setIsLoading(true);
      const response = await getOrders(userId, sortMethod);

      if (isMounted.current) {
        handleStateChange({
          yourOrders: response,
        });
      }
    } catch (err) {
      handleStateChange({
        yourOrders: [],
      });
      setErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchYourOrdersById = useCallback(async (orderId) => {
    handleStateChange({
      orderItems: [],
    });
    try {
      setIsLoading(true);
      const response = await search(
        enums.models.orders,
        {
          client: mainClient.id,
          _id: orderId,
        },
        { isDivisionControlled: mainClient?.isDivisionControlled }
      );

      if (isMounted.current) {
        handleStateChange({
          orderItems: response[0],
        });
      }
    } catch (err) {
      handleStateChange({
        orderItems: [],
      });
      setErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const searchCriteriaProducts = useCallback(
    async (searchText, pageNo, perPageRecords, category_id, sort) => {
      try {
        setIsProductLoading(true);
        const response = await searchProducts(
          searchText,
          pageNo,
          perPageRecords,
          category_id,
          sort
        );
        if (isMounted.current) {
          handleStateChange({
            clientProducts: response,
          });
        }
      } catch (err) {
        setErrorMessage(err);
      } finally {
        setIsProductLoading(false);
      }
    },
    []
  );

  const fetchWishlist = useCallback(
    async (searchText, pageNo, perPageRecords, category_id, sort) => {
      const userId = localStorageUtils.get(localStorageKeys.userDetails)?._id;
      try {
        setIsWishListLoading(true);
        const response = await getWishList(userId);

        setWishlistItems(response[0]);
        handleStateChange({
          wishlists: response,
        });
        return response;
      } catch (err) {
        setErrorMessage(err);
      } finally {
        setIsWishListLoading(false);
      }
    },
    []
  );

  const fetchSpecialProducts = useCallback(
    async (searchText, pageNo, perPageRecords, category_id, sort) => {
      try {
        setIsProductLoading(true);
        const response = await getSpecialProducts();

        if (isMounted.current) {
          setWishlistItems(response[0]);
          handleStateChange({
            specialProducts: response,
          });
        }
      } catch (err) {
        setErrorMessage(err);
      } finally {
        setIsProductLoading(false);
      }
    },
    []
  );

  const getAppPushNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await setPushNotifications();

      if (isMounted.current) {
        // setWishlistItems(response[0]);
        handleStateChange({
          alerts: response,
        });
      }
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addToCart = async (product, inputFields = {}) => {
    // const existingCartItems = [];
  };

  const setSelectedProduct = (product) => {
    localStorageUtils.set(localStorageKeys.selectedProduct, product);
  };

  const setSelectedDivision = (division) => {
    localStorageUtils.set(localStorageKeys.division, division);
  };

  const setSelectedCategory = (category) => {
    localStorageUtils.set(localStorageKeys.category, category);
  };

  useEffect(() => {
    handleStateChange({
      selectedProduct:
        localStorageUtils.get(localStorageKeys.selectedProduct) || {},
    });

    handleStateChange({
      selectedCategory: localStorageUtils.get(localStorageKeys.category) || {},
    });

    handleStateChange({
      yourCartItems: localStorageUtils.get(localStorageKeys.cartItems) || {},
    });

    const cartItems = localStorageUtils.get(localStorageKeys.cartItems);
    if (!isEmpty(cartItems?.items)) {
      setIsCartItemsAdded(true);
    } else {
      setIsCartItemsAdded(false);
    }

    if (isEmpty(localStorageUtils.get(localStorageKeys.wishListItems))?.items) {
      setIsWishListItemChanged(false);
    } else {
      setIsWishListItemChanged(true);
    }
  }, [localStorage]);

  const apiContextValues = useMemo(
    () => ({
      fetchSpecialProducts,
      isCartItemsAdded,
      setIsCartItemsAdded,
      fetchOverview,
      state,
      fetchDivisions,
      fetchClientProducts,
      fetchClientProductsPaging,
      isLoading,
      errorMessage,
      fetchAllCategories,
      setSelectedProduct,
      addToCart,
      setSelectedDivision,
      setSelectedCategory,
      fetchProductByCode,
      fetchYourOrders,
      setIsOrderPlacing,
      isOrderPlacing,
      searchCriteriaProducts,
      setSearchText,
      fetchWishlist,
      setIsWishListItemChanged,
      wishListItems,
      setWishlistItems,
      isWishListItemChanged,
      setClientDivisions,
      clientDivisions,
      getAppPushNotifications,
      isWishListLoading,
      setIsWishListLoading,
      cartItemItems,
      setCartItemItems,
      fetchYourOrdersById,
      setIsProductLoading,
      isProductLoading,
      isSingleProductLoading,
      setIsSingleProductLoading,
    }),
    [
      isSingleProductLoading,
      setIsSingleProductLoading,
      setIsProductLoading,
      isProductLoading,
      fetchYourOrdersById,
      cartItemItems,
      setCartItemItems,
      isWishListLoading,
      setIsWishListLoading,
      getAppPushNotifications,
      fetchSpecialProducts,
      setClientDivisions,
      clientDivisions,
      wishListItems,
      setWishlistItems,
      isWishListItemChanged,
      isCartItemsAdded,
      setSearchText,
      fetchOverview,
      state,
      fetchDivisions,
      fetchClientProducts,
      fetchClientProductsPaging,
      isLoading,
      errorMessage,
      fetchAllCategories,
      fetchProductByCode,
      fetchYourOrders,
      setIsOrderPlacing,
      isOrderPlacing,
      searchCriteriaProducts,
      fetchWishlist,
      setIsWishListItemChanged,
    ]
  );

  return (
    <APIContext.Provider value={apiContextValues}>
      {children}
    </APIContext.Provider>
  );
};

export const useAppApi = () => useContext(APIContext);
